import React, { useState, useEffect } from 'react';
import type ApolloClient from 'apollo-client';
import SectionMessage from '@atlaskit/section-message';
import { useIntl, defineMessages } from 'react-intl-next';

import {
	type Extension,
	GET_APP_NAME,
	type AppDetails,
	type AppDetailsQueryParams,
} from '../web-client';
import { createAtlAttributionHeader } from '../utils';

interface ForgeModalDeprecationMessageProps {
	client?: ApolloClient<any>;
	extension?: Extension;
}

/**
 * This message is displayed when a forge custom field is using a deprecated modal experience for rendering on the Jira issue view.
 * It is displayed in a modal dialog and is not dismissible.
 * It is one of the steps related to the deprecation of the modal experience which was decided here:
 * https://hello.atlassian.net/wiki/spaces/Ditto/pages/5092641172/DACI+-+Modal+experience+for+the+Forge+custom+fields+on+issue+view+clean-up?focusedCommentId=5111022370
 */
export function ForgeModalDeprecationMessage(
	props: ForgeModalDeprecationMessageProps,
): React.JSX.Element {
	const { client, extension } = props;
	const [appName, setAppName] = useState<string | undefined>(undefined);

	useEffect(() => {
		if (client && extension) {
			const response = client.query<AppDetails, AppDetailsQueryParams>({
				query: GET_APP_NAME,
				variables: { id: extension.id },
				context: {
					headers: {
						'atl-attribution': createAtlAttributionHeader(),
					},
				},
			});

			response.then((data) => {
				const appName = data.data?.app.name;

				setAppName(appName);
			});
		}
	}, [client, extension]);

	const intl = useIntl();

	return (
		<SectionMessage title={intl.formatMessage(messages.title)} appearance="error">
			{intl.formatMessage(messages.message, { appName })}
		</SectionMessage>
	);
}

const messages = defineMessages({
	title: {
		id: 'forge.modal.deprecation.title',
		defaultMessage: 'Update your app',
		description: 'A section title that informs the user that the app needs to be updated.',
	},
	message: {
		id: 'forge.modal.deprecation.message',
		defaultMessage:
			'Your app {appName} uses the deprecated modal editing experience for custom fields, which is being phased out. Please have your system admin update the app to use inline editing instead. If issues remain, contact the app developer.',
		description:
			'A section message that informs the user that the app needs to be updated as it is using a deprecated platform feature and will soon stop working.',
	},
});
